import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const COLORS = {
    titleBlue:    "FF2F75B5",
    headerBg:     "FF1F4E79",
    headerFont:   "FFFFFFFF",
    maleBg:       "FFD6E4F7",
    femaleBg:     "FFFFE0F0",
    totalBg:      "FFFFEB9C",
    summaryHeader:"FF4472C4",
    summaryRowOdd:"FFF2F7FF",
    borderColor:  "FFB8CCE4",
};

const border = () => ({
    top:    { style: "thin", color: { argb: COLORS.borderColor } },
    left:   { style: "thin", color: { argb: COLORS.borderColor } },
    bottom: { style: "thin", color: { argb: COLORS.borderColor } },
    right:  { style: "thin", color: { argb: COLORS.borderColor } },
});

const pad = (n) => String(n).padStart(2, "0");
const generatedAt = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const groupByBranch = (dataList) => {
    const grouped = {};
    dataList.forEach((row) => {
        const key = row.locationName || row.branchName || row.branchId || "Unknown";
        if (!grouped[key]) {
            grouped[key] = {
                branchName: row.locationName || row.branchName || key,
                male:   0,
                female: 0,
                total:  0,
            };
        }
        const entry = grouped[key];

        const genderField = (row.gender || "").toUpperCase();
        const male   = Number(row.maleCount   ?? row.male   ?? (genderField === "MALE"   ? (row.count ?? 0) : 0))   || 0;
        const female = Number(row.femaleCount ?? row.female ?? (genderField === "FEMALE" ? (row.count ?? 0) : 0))   || 0;
        entry.male   += male;
        entry.female += female;
        entry.total   = entry.male + entry.female;
    });
    return Object.values(grouped);
};

const SANITIZE_SHEET_NAME = (name) =>
    (name || "").substring(0, 31).replace(/[\\/*?:\x5B\x5D]/g, "_");

const writeBranchSheet = (workbook, branch, filters, idx, usedNames) => {
    let wsName = SANITIZE_SHEET_NAME(branch.branchName) || `Branch_${idx + 1}`;
    let unique = wsName;
    let suffix = 1;
    while (usedNames.has(unique)) {
        suffix += 1;
        unique = `${wsName.substring(0, 28)}_${suffix}`;
    }
    usedNames.add(unique);

    const ws = workbook.addWorksheet(unique);

    const reportTitle = filters.reportTitle || "Samurthi Bank Gender Wise Customer Details";

    ws.mergeCells("A1:D1");
    Object.assign(ws.getCell("A1"), {
        value: reportTitle,
        font:  { bold: true, size: 16, color: { argb: COLORS.titleBlue } },
        alignment: { horizontal: "center", vertical: "middle" },
    });
    ws.getRow(1).height = 30;

    ws.mergeCells("A2:D2");
    Object.assign(ws.getCell("A2"), {
        value: `Branch: ${branch.branchName}`,
        font:  { bold: true, size: 13, color: { argb: "FF1F4E79" } },
        alignment: { horizontal: "center", vertical: "middle" },
    });
    ws.getRow(2).height = 22;

    const filterLines = [
        `District: ${filters.district   || "All"}`,
        `Divisional: ${filters.divisional || "All"}`,
        `Generated: ${generatedAt()}`,
    ];
    filterLines.forEach((text, i) => {
        ws.mergeCells(`A${3 + i}:D${3 + i}`);
        Object.assign(ws.getCell(`A${3 + i}`), {
            value: text,
            font:  { size: 10, italic: true, color: { argb: "FF595959" } },
            alignment: { horizontal: "left" },
        });
    });

    // ── Table header ─────────────────────────────────────────────────────
    const hRow = ws.getRow(7);
    ["Category", "Gender", "Count", "Percentage"].forEach((h, i) => {
        const cell = hRow.getCell(i + 1);
        cell.value = h;
        cell.font  = { bold: true, color: { argb: COLORS.headerFont } };
        cell.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.headerBg } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = border();
    });
    hRow.height = 20;

    const total = branch.total || 0;
    const malePercent   = total > 0 ? ((branch.male   / total) * 100).toFixed(2) + "%" : "0.00%";
    const femalePercent = total > 0 ? ((branch.female / total) * 100).toFixed(2) + "%" : "0.00%";

    const dataRows = [
        ["Gender Breakdown", "Male",   branch.male,   malePercent,   COLORS.maleBg],
        ["Gender Breakdown", "Female", branch.female, femalePercent, COLORS.femaleBg],
    ];

    dataRows.forEach(([cat, gender, count, pct, bg], ri) => {
        const row = ws.getRow(8 + ri);
        [cat, gender, count, pct].forEach((val, ci) => {
            const cell = row.getCell(ci + 1);
            cell.value = val;
            cell.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
            cell.border = border();
            cell.alignment = { horizontal: ci >= 2 ? "center" : "left", vertical: "middle" };
        });
        row.height = 18;
    });

    // Total row
    const tRow = ws.getRow(10);
    ["Total", "All", total, "100.00%"].forEach((val, ci) => {
        const cell = tRow.getCell(ci + 1);
        cell.value = val;
        cell.font  = { bold: true };
        cell.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.totalBg } };
        cell.border = border();
        cell.alignment = { horizontal: ci >= 2 ? "center" : "left", vertical: "middle" };
    });
    tRow.height = 20;

    // Column widths
    [28, 14, 12, 14].forEach((w, i) => { ws.getColumn(i + 1).width = w; });
};

const writeSummarySheet = (workbook, branches, filters) => {
    const ws = workbook.addWorksheet("Summary", {
        properties: { tabColor: { argb: "FF4472C4" } },
    });

    const reportTitle = filters.reportTitle || "Samurthi Bank Gender Wise Customer Details";

    ws.mergeCells("A1:E1");
    Object.assign(ws.getCell("A1"), {
        value: reportTitle,
        font:  { bold: true, size: 16, color: { argb: COLORS.titleBlue } },
        alignment: { horizontal: "center", vertical: "middle" },
    });
    ws.getRow(1).height = 30;

    ws.mergeCells("A2:E2");
    Object.assign(ws.getCell("A2"), {
        value: `District: ${filters.district || "All"}  |  Divisional: ${filters.divisional || "All"}  |  Branches: ${filters.branches || "All"}`,
        font:  { size: 11, italic: true },
        alignment: { horizontal: "center" },
    });

    ws.mergeCells("A3:E3");
    Object.assign(ws.getCell("A3"), {
        value: `Generated: ${generatedAt()}`,
        font:  { size: 10, italic: true, color: { argb: "FF595959" } },
        alignment: { horizontal: "right" },
    });

    // Table header
    const hRow = ws.getRow(5);
    ["No.", "Branch Name", "Male", "Female", "Total"].forEach((h, i) => {
        const cell = hRow.getCell(i + 1);
        cell.value = h;
        cell.font  = { bold: true, color: { argb: COLORS.headerFont } };
        cell.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.summaryHeader } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = border();
    });
    hRow.height = 22;

    let totMale = 0, totFemale = 0, totAll = 0;

    branches.forEach((branch, idx) => {
        totMale   += branch.male;
        totFemale += branch.female;
        totAll    += branch.total;

        const row = ws.getRow(6 + idx);
        [idx + 1, branch.branchName, branch.male, branch.female, branch.total].forEach((val, ci) => {
            const cell = row.getCell(ci + 1);
            cell.value = val;
            cell.fill  = {
                type: "pattern", pattern: "solid",
                fgColor: { argb: idx % 2 === 0 ? COLORS.summaryRowOdd : "FFFFFFFF" },
            };
            cell.border = border();
            cell.alignment = { horizontal: ci === 1 ? "left" : "center", vertical: "middle" };
        });
        row.height = 18;
    });

    // Grand-total row
    const tRowNum = 6 + branches.length;
    ws.mergeCells(`A${tRowNum}:B${tRowNum}`);
    ["Total", null, totMale, totFemale, totAll].forEach((val, ci) => {
        if (ci === 1) return; // merged
        const cell = ws.getCell(tRowNum, ci + 1);
        cell.value = val;
        cell.font  = { bold: true };
        cell.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.totalBg } };
        cell.border = border();
        cell.alignment = { horizontal: ci === 0 ? "center" : "center", vertical: "middle" };
    });
    ws.getRow(tRowNum).height = 20;

    // Column widths
    [8, 36, 12, 12, 12].forEach((w, i) => { ws.getColumn(i + 1).width = w; });
};

export const generateGenderWiseReportExcel = async (
    apiData   = [],
    filters   = {},
    fileName  = "GenderWiseReport"
) => {
    if (!Array.isArray(apiData) || apiData.length === 0) {
        console.warn("generateGenderWiseReportExcel: no data");
        return;
    }

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "E-Bank MIS";
    workbook.created = new Date();

    const branches = groupByBranch(apiData);

    const usedNames = new Set(["Summary"]);
    writeSummarySheet(workbook, branches, filters);
    branches.forEach((branch, idx) => writeBranchSheet(workbook, branch, filters, idx, usedNames));

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
        new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `${fileName}.xlsx`
    );
};
